import { useMemo } from 'react';
import {
  NoShowRateByType,
  NoShowRateRespParams,
  RoiType,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { AnalyticsGatewayV1 } from '@frontend/api-analytics-gateway';
import { useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { featureFlags } from '../../../../feature-flags';
import { commonApiOptions } from '../../helpers/api-helpers';
import { useRoiRequestPayload, type UseRoiRequestBaseProps } from '../../hooks/use-roi-request-payload';
import { generateNSRTimelineDemoData } from '../demo-data/overview';

interface Props extends UseRoiRequestBaseProps {
  additionalRequestPayload: {
    roiType: RoiType;
    noShowRateType: NoShowRateByType;
  };
}

interface UseFetchNSRROITimeline {
  aggregates?: Record<string, number>;
  isLoading?: boolean;
  rawData?: AnalyticsGatewayV1.Types.GetNoShowRateIO['output'];
}

export const getTimelineValuePair = (params?: NoShowRateRespParams[]) => {
  return (
    params?.reduce<Record<string, number>>((acc, { name = '', value = 0 }) => ({ ...acc, [name]: value }), {}) || {}
  );
};

export const useFetchNSRROITimeline = (props: Props): UseFetchNSRROITimeline => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.noShowRateRoiDemoData);

  const { request, skipRequest } = useRoiRequestPayload({
    ...props,
    isDemoAccount,
  });

  const { data: noShowRateResponse, isLoading } = AnalyticsGatewayV1.Queries.useGetNoShowRateQuery({
    request,
    options: {
      ...commonApiOptions,
      enabled: !skipRequest,
      onError: () => {
        alert.error(t("Couldn't fetch the no-show rate details. Please try again."));
      },
    },
  });

  const { aggregates, rawData } = useMemo(() => {
    const dataToUse = isDemoAccount
      ? generateNSRTimelineDemoData(props.additionalRequestPayload.noShowRateType)
      : noShowRateResponse;

    if (!dataToUse) {
      return {};
    }

    const data = dataToUse.data;

    return {
      aggregates:
        data?.aggregates?.reduce<Record<string, number>>(
          (acc, { name = '', value = 0 }) => ({
            ...acc,
            [name || 'other']: value,
          }),
          {}
        ) || {},
      rawData: dataToUse,
    };
  }, [isDemoAccount, noShowRateResponse]);

  return {
    aggregates,
    isLoading,
    rawData,
  };
};
